
import { Component, Inject, OnInit, SkipSelf } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  AuthService,
  LogService,
  OfferService,
  PrintService,
  ProfileService,
  StyleService,
  TemplateService,
} from '../../services';
import { catchError, combineLatest, lastValueFrom, of, Subject, switchMap, tap } from 'rxjs';
import { cloneDeep } from 'lodash';
import { MatSnackBar } from '@angular/material/snack-bar';
import { VoucherStatus } from '../../models/voucher-status.enum';
import { SnackbarComponent } from '../snackbar/snackbar.component';
import * as moment from 'moment-timezone';
import momentAll from 'moment';
import { str } from "crc-32";
import { BreakOfferConfig } from 'projects/builder-app/src/app/feature/cms-offers/components/models/cms-offer-detail';
import { LogSource, LogType } from '../../models/common';
import { PopUpComponent } from '../pop-up/pop-up.component';

enum SessionStorageItems {
  PrintedOffers = 'printedOffers',
  FailedPrintOffers = 'failedPrintOffers',
}

@Component({
  selector: 'app-cms-offer-modal',
  templateUrl: './cms-offer-modal.component.html',
  styleUrls: ['./cms-offer-modal.component.scss'],
})
export class CmsOfferModalComponent implements OnInit {
  public printCounter: number = 0;
  public isLoading: boolean = false;
  public redeemedOfferDisplaySettings: any;
  public voucherNumber: string = '';
  public voucherImage: string = '';
  public printOnScreen: boolean = false;
  public redeemResponse: any = {};
  public oldOffer: any = {};

  public isSwapMode: boolean = false;

  public swapItems: 4|6 = 4;
  public isBreakValueMode: boolean = false;
  public breakOptions: BreakOfferConfig= {};
  public couponCost: number = 0;
  public ticketAmount: number = 0;
  public isMultiPrint: boolean = false;
  public advPrint: boolean = false;
  public advPrintKiosks: string = "";
  public breakValue$: Subject<any> = new Subject();

  public swapItemsList: any = [];
  public swapRewardOptions = {
    webhook: "",
    printTemplate: ""
  }

  private list!: any;
  private ticketPrinted: number = 0;

  public get isOfferRedeemed() {
    return this.data.offer.couponStatus === 'Redeemed';
  }

  public get isPrintButtonHidden() {
    return (
      !!this.getSessionStorageItem(
        this.data.offer.id,
        SessionStorageItems.PrintedOffers
      ) ||
      !!this.getSessionStorageItem(
        this.data.offer.id,
        SessionStorageItems.FailedPrintOffers
      ) ||
      this.printCounter === 3
    );
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    @SkipSelf() public styleService: StyleService,
    private readonly printService: PrintService,
    private profileService: ProfileService,
    private offerService: OfferService,
    private snackBar: MatSnackBar,
    private logService: LogService,
    private readonly dialogRef: MatDialogRef<any>,
    private templateService: TemplateService,
    private authService: AuthService,
    private readonly dialog: MatDialog,
  ) {}

  public get isBreakable() {
    return this.data.offer.extendedDetails.isSplittable;
  }

  public get isSwappable() {
    const swapRewardOptions = this.data.offer.extendedDetails.isSwapReward;
    return (!swapRewardOptions && this.swapItemsList.length > 0);
  }

  ngOnInit() {
    this.setSessionStorageProps();
    this.redeemedOfferDisplaySettings =
      this.data.tab.configurations.main.redeemedOfferDisplaySettings;
    this.couponCost = parseInt(this.data.offer.value);
    // this.couponCost = 10;

    this.printOnScreen = this.data.tab.configurations?.voucher?.printOnScreen?.isTestMode;
    this.advPrint = this.data.tab.configurations?.voucher?.advPrint?.isEnabled;
    this.advPrintKiosks = this.data.tab.configurations?.voucher?.advPrint?.kioskDeviceIds;
    this.voucherImage = this.data.tab.configurations?.voucher?.voucherImage?.background?.imageLink;
    this.oldOffer = this.data.offer.extendedDetails;

    this.printService.isNewPrintVersion = this.advPrint && 
      (
        this.advPrintKiosks.split(",").findIndex((dev) => dev.trim() === this.authService.deviceId) > -1 || 
        !this.advPrintKiosks
      )

    this.populateSwap();

    this.breakValue$.pipe(
      switchMap((event) => combineLatest(
        this.offerService.redeemCmsOffer(this.data.offer),
        of(event)
      )),
      tap((data: any) => {
        const response = data[0];
        const event = data[1];

        this.logService.writeLog(`Selected break value ${JSON.stringify(event)}`, LogType.DEBUG, LogSource.BREAK_OFFER);

        if (!response?.errors?.length || response?.status === 'Redeemed') {
          this.data.offer.couponStatus = 'Redeemed';
          this.snackBar.open(`${this.offerService.successRedeemMessage}`, '', {
            panelClass: 'success',
            duration: 3000,
            horizontalPosition: 'end',
          });

          this.list = [];
          this.ticketPrinted = 0;

          Object.keys(event).forEach((key) => {
            for (let i = 0; i < event[key].qty; i++) {
              this.list.push({ amount: event[key].amt })
            }
          });

          if (this.printService.isNewPrintVersion) {
            this.printMultiVouchers();
          } else {
            let intervalFunc: any = null;
            intervalFunc = setInterval(() => {
              this.ticketAmount = this.list[this.ticketPrinted].amount;
              this.printPage();
              this.ticketPrinted = this.ticketPrinted + 1;

              this.logService.writeLog(`Printed ${this.ticketAmount} number ${this.ticketPrinted} out of ${this.list.length}`, LogType.DEBUG, LogSource.BREAK_OFFER);

              if (this.ticketPrinted === this.list.length) {
                setTimeout(() => {
                  this.printService.setIsPrinting(false, "Break Value");
                }, this.offerService.timeoutPeriod * 1000);
                clearInterval(intervalFunc);
              }
            }, this.offerService.timeoutPeriod * 1000)
          }

          this.dialogRef.close();
        } else {
          this.handleRedeemError(response?.errors[0]?.message);
        }
      }),
      catchError((error) => {
        this.handleRedeemError(error);
        return of(null);
      })
    ).subscribe();       
  }

  private sleep(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  private async printMultiVouchers() {
    for (let i = 0; i < this.list.length; i++) {
      this.ticketAmount = this.list[this.ticketPrinted].amount;
      await this.printPage();
      await this.sleep(4000);

      this.ticketPrinted = this.ticketPrinted + 1;
      console.log("Completed: ", this.ticketPrinted);
      this.logService.writeLog(`Printed ${this.ticketAmount} number ${this.ticketPrinted} out of ${this.list.length}`, LogType.DEBUG, LogSource.BREAK_OFFER);
    }
  }

  setSlides(cmsOffersList: any[], size: number) {
    for (let i = 0; i < Math.ceil(cmsOffersList.length / size); i++) {
      this.swapItemsList[i] = cmsOffersList.slice(i * size, i * size + size);
    }
  }

  private async populateSwap() {
    const configs = this.templateService.templateData.advancedSettings.swapOfferConfig;
    const configList = JSON.parse(configs);
    const keys = Object.keys(configList);
    let listOfItems: any = [];

    for (let i = 0; i < keys.length; i++) {
      if (this.data.offer.type === configList[keys[i]].type) {
          const cmsOfferListFull = (await this.offerService.getCmsOfferDetails({
            identifiers: configList[keys[i]].options,
            status: ['Active'],
          }).toPromise());

          if (cmsOfferListFull.items.length) {
            for (let j = 0; j < cmsOfferListFull.items.length; j++) {
              cmsOfferListFull.items[j].offerDecorator.title = 
                cmsOfferListFull.items[j].offerDecorator.title.replace('{amount}', `$${this.couponCost.toString()}`)
              cmsOfferListFull.items[j].offerDecorator.iconUrl = cmsOfferListFull.items[j].offerDecorator.imageUrl;
              listOfItems.push(cmsOfferListFull.items[j].offerDecorator);
            }
          }
      }
    }

    this.setSlides(listOfItems, this.swapItems);
  }

  public formatDate(dateString: string): string {
    const clientTimeZone = moment.tz.guess();
    return moment
      .tz(dateString, clientTimeZone)
      .format('MM DD, YYYY [at] hh:mm A');
  }

  private setSessionStorageProps() {
    Object.values(SessionStorageItems).forEach((value) => {
      if (!sessionStorage.getItem(value)) {
        sessionStorage.setItem(value, JSON.stringify([]));
      }
    });
  }

  public getSessionStorageItem(id: string, type: string) {
    const storageValues = sessionStorage.getItem(type);
    if (storageValues?.length) {
      return (JSON.parse(storageValues) as []).find(
        (offerId) => offerId === id
      );
    }
  }

  public setSessionStorageItem(id: string, type: string) {
    const storageValues: string[] = JSON.parse(sessionStorage.getItem(type)!);
    storageValues.push(id);
    sessionStorage.setItem(type, JSON.stringify(storageValues));
  }

  public get getPrintButtonText(): string {
    const printBtn =
      this.data.tab.configurations.cardOfferDetails?.buttonPrintOfferText;
    return printBtn?.length ? printBtn : 'Print';
  }

  public get getRedeemButtonText(): string {
    const redeemBtn =
      this.data.tab.configurations.cardOfferDetails?.buttonRedeemOfferText;
    return redeemBtn?.length ? redeemBtn : 'Redeem';
  }

  public get getSwapBtnText(): any {
    const swapBtn =
      this.data.tab.configurations.swapOfferCms?.buttonTrade;
    return swapBtn?.length ? swapBtn : 'Swap Offer';
  }

  public get getBreakValueText(): string {
    const breakBtn =
      this.data.tab.configurations.cardOfferDetails?.buttonBreakValueText;
    return breakBtn?.length ? breakBtn : 'Break Value';
  }

  public get getPrintRedeemStyle(): any {
    const offerDetais = this.data.tab.configurations?.cardOfferDetails;
    return offerDetais.buttonPrintOffer ?? offerDetais.redeemOfferButton;
  }

  public get getRedeemStyle(): any {
    const offerDetais = this.data.tab.configurations?.cardOfferDetails;
    return offerDetais.redeemOfferButton;
  }

  public get getSwapStyle(): any {
    const offerDetais = this.data.tab.configurations?.swapOfferCms;
    return offerDetais?.buttonTradeConfig;
  }

  public get getBreakValueStyle(): any {
    const breakValueStyle = this.data.tab.configurations?.cardOfferDetails;
    return breakValueStyle.buttonBreakValue;
  }

  public exitSwapMode() {
    this.isSwapMode = false;
    this.isBreakValueMode = false;
    this.couponCost = parseInt(this.data.offer.value);
  }

  public showBreakOptions() {
    this.isBreakValueMode = true;
    const all_breakoptions = JSON.parse(this.data.offer.extendedDetails.splitOfferOptions);

    const legitKeys = Object.keys(all_breakoptions).filter((key) => {
      const cur_obj = all_breakoptions[key];
      let runningTotal = 0;

      Object.values(cur_obj).forEach((val: any) => {
        runningTotal += val.total;
      })

      if (runningTotal === this.couponCost) {
        return key;
      }
    })

    this.logService.writeLog(`Break options: ${JSON.stringify(all_breakoptions)}`)

    this.breakOptions = Object.fromEntries(
      Object.entries(all_breakoptions).filter(([key]) => legitKeys.includes(key))) as {
      [key: string]: { qty: number, amt: number, total: number }[]
    };
  }

  public selectItem(event: any) {
    let ticketCost = 0;
    Object.keys(event).forEach((key) => {
      ticketCost += event[key].qty * event[key].amt;
    });

    this.printService.setIsPrinting(true, "Break Value");
    this.isMultiPrint = true;

    if (this.couponCost !== ticketCost) {
      this.printService.setIsPrinting(false, "Break Value");
      this.snackBar.open(`Redemption failed: The ticket cost does not match the coupon cost.`, '', {
        panelClass: 'error',
        duration: 5000,
        horizontalPosition: 'end',
      });
    } else {
      this.breakValue$.next(event);
    }
  }

  private generateFilledHtml(template: string, params: any): string {
    return template.replace(/\{\{(.*?)\}\}/g, (_, key) => {
      const value = params[key.trim()];
      return value != null ? value : '';
    });
  }

  public get isPrinting() {
    return this.printService.isPrinting;
  }

  public async printPage() {
    if (this.printCounter === 2 && !this.isMultiPrint) {
      this.setSessionStorageItem(
        this.data.offer.id,
        SessionStorageItems.FailedPrintOffers
      );
      lastValueFrom(
        this.offerService.setVoucherStatus(
          VoucherStatus.Error,
          this.voucherNumber
        )
      );
    }

    ++this.printCounter;
    this.isLoading = true;

    const [pageIndex, templateIndex] = this.data.indexes;

    const details = this.data.offer.extendedDetails;
    const profile = this.profileService.playerProfile;
    if (!this.voucherNumber) {
      const { number } = (await lastValueFrom(
        this.offerService.getVoucherUniqCode()
      )) as any;
      this.voucherNumber = number;
    }

    let redeemResult = null;
    const clientTimeZone = moment.tz.guess();
    this.printService.setIsPrinting(true, "CMS Offer printPage");

    this.snackBar.open(`${this.offerService.loadingPrintMessage}`, '', {
      panelClass: 'success',
      duration: 3000,
      horizontalPosition: 'end',
    });

    if (
      this.data.offer.extendedDetails.isRedeemable &&
      this.data.offer.couponStatus !== 'Redeemed'
    ) {
      redeemResult = await this.redeemReward(true);
      // await this.redeemReward(true); 
    }

    this.logService.writeLog(`Redeem result: ${JSON.stringify(redeemResult)}`, LogType.DEBUG, LogSource.PRINT);

    if (!!redeemResult && JSON.stringify(this.redeemResponse).toString() === "{}") {
      this.closeModal();
      this.handleRedeemError({});
      return;
    }

    const expiryDate = momentAll().add(24, 'hours');
    const expiryDateFormatted = expiryDate.format('YYMMDDHHmmss');
    const swapRewardOptions = this.data.offer.extendedDetails.isSwapReward;

    const rawTemplate =
      this.data.offer.isSwapped ?
        JSON.parse(swapRewardOptions)?.printTemplate :
        (this.data.tab.configurations.voucher.cmsHtml ??
        this.data.tab.configurations.voucher.html);

    const printData = {
      pageIndex,
      templateIndex,
      voucher: this.voucherNumber,
      voucherImage: this.voucherImage,
      title: details.title,
      playerId: profile.playerId,
      playerName: this.profileService.fallBackDataPointValues().playerName,
      ticketNumber: this.voucherNumber,
      cmsOfferEndDate: moment
        .tz(this.data.offer.endDate, clientTimeZone)
        .format('MM DD, YYYY'),
      cmsOfferEndTime: moment
        .tz(this.data.offer.endDate, clientTimeZone)
        .format('hh:mm A'),
      nowDate: momentAll().format('MM/DD/YYYY HH:mm:ss'),
      nowDate12h: momentAll().format('MM/DD/YYYY hh:mm:ss A'),
      expiryDate: expiryDate.format('MM/DD/YYYY HH:mm:ss'),
      expiryDateFormatted,
      qrOfferId: this.redeemResponse?.offerId,
      qrAmount: this.redeemResponse?.amount,
      amount: this.ticketAmount,
      ...(this.data.offer.isSwapped && { externalIdentifier: encodeURIComponent(this.data.offer.extendedDetails.externalIdentifier) }),
      crc32hash: (str(`${profile.playerId}${expiryDateFormatted}${this.redeemResponse.offerId}!!NRT!!`) >>> 0).toString(16).padStart(8, '0').toUpperCase()
    };

    const page = this.printService.preparePage(
      rawTemplate,
      printData,
      this.data.tab.configurations.voucher.cmsHtml ? 'cmsHtml' : 'html'
    );

    this.logService.writeLog(`Start printing cms offer ${JSON.stringify(printData)}`, LogType.INFO, LogSource.PRINT);

    if (this.printOnScreen) {
      const filledHtml = this.generateFilledHtml(rawTemplate, printData);
      const previewWindow = window.open('', '_blank');
      previewWindow?.document.write(filledHtml);
      previewWindow?.document.close();
      this.printService.setIsPrinting(false, "CMS Offer print on screen");
      this.isLoading = false;
      return; 
    }

    return (this.printService.isNewPrintVersion ? this.printService.printPage_V2(page) : this.printService.printPage(page)).then(() => {
      this.isLoading = false;
      this.closeModal(false);
      const printedOffersSession = this.getSessionStorageItem(this.data.offer.id, SessionStorageItems.PrintedOffers);

      if (!printedOffersSession) {
        this.setSessionStorageItem(
          this.data.offer.id,
          SessionStorageItems.PrintedOffers
        );
        lastValueFrom(
          this.offerService.setVoucherStatus(
            VoucherStatus.Printed,
            this.voucherNumber
          )
        );
      }

      this.snackBar.open(`${this.offerService.successPrintMessage}`, '', {
        panelClass: 'success',
        duration: 5000,
        horizontalPosition: 'end',
      });

      if (this.printService.isNewPrintVersion) {
        if (this.isMultiPrint && this.ticketPrinted === this.list.length - 1) {
          this.printService.setIsPrinting(false, "Break Value");
        }
      }

      if (!this.isMultiPrint) {
        setTimeout(() => {
          this.printService.setIsPrinting(false, "CMS Offer printPage timeout");
          this.isLoading = false;
        }, this.offerService.timeoutPeriod * 1000);
      }
    }).catch((err) => {
      if (this.printService.isNewPrintVersion) {
        console.log("catch error: ", err);
        if (err === "PRINTFAIL" && this.isMultiPrint) {
          this.isLoading = false;
          this.printService.setIsPrinting(false, "Break Value Fail");

          this.dialog.open(PopUpComponent, {
            panelClass: 'pop-up-dialog',
            disableClose: true,
            backdropClass: ['backDropClass', 'popUpClass'],
            data: { 
              message: `Print process failed. ${this.ticketPrinted} out of ${this.list.length} vouchers were printed successfully. Please see Player Services at the Cashier Cage.`
            }
          });

          this.ticketPrinted = 0;
          this.list = [];
        } else {
          this.handlePrintFailed(err);
        }
      } else {
        this.handlePrintFailed(err);
      }
    });
  }
  private handlePrintFailed(e: any) {
    this.isLoading = false;
    this.printService.setIsPrinting(false, "CMS Offer printPage err");
    if (e.message === 'Timeout') {
      this.logService.writeLog(
        'Start printing cms offer failed due to timeout',
        LogType.ERROR,
        LogSource.PRINT
      );
    } else {
      this.snackBar.openFromComponent(SnackbarComponent, {
        panelClass: 'error',
        duration: 5000,
        horizontalPosition: 'end',
        data: {
          type: 'error',
          message: `Print failed. Please try again.`,
        },
      });
    }
    
    lastValueFrom(
      this.offerService.setVoucherStatus(
        VoucherStatus.Error,
        this.voucherNumber
      )
    );
  }

  public closeModal(isPrinting: boolean = true): void {
    if (isPrinting) {
      this.printService.setIsPrinting(false, "CMS Offer closeModal")
    }

    this.isSwapMode = false;
    this.logService.writeLog(`Close modal CMS Offer`, LogType.INFO, LogSource.MODAL);
    this.dialogRef.close();
  }

  public openSwapModal() {
    this.logService.writeLog(`Swap items list: ${JSON.stringify(this.swapItemsList)}`);

    // OC-477 show break options instantly if only item in list
    if (this.swapItemsList[0].length === 1 && this.swapItemsList[0][0].isSplittable) {
      this.swapItemsList[0][0].amount = this.couponCost.toString();
      this.data.offer.extendedDetails = this.swapItemsList[0][0];
      this.data.offer.isSwapped = true;
      this.logService.writeLog(`Jumping to break value page`);
      this.showBreakOptions();
    } else {
      this.logService.writeLog(`Jumping to swap items page`);
      this.isSwapMode = true;
    }
  }

  exitBreakValue() {
    this.isBreakValueMode = false; 

    // if gone from swap -> breakvalue
    if (this.data.offer.extendedDetails.externalIdentifier !== this.oldOffer.externalIdentifier) {
      const hasOneItem = this.swapItemsList[0].length > 1;

      if (hasOneItem) {
        this.isSwapMode = true; 
      } else {
        this.isSwapMode = false;
      }
    }

    this.isMultiPrint = false; 
    this.data.offer.extendedDetails = this.oldOffer;
    this.couponCost = parseInt(this.oldOffer.amount);
  }

  public swapAndRedeem(item: any) {
    this.data.offer.isSwapped = true;
    this.data.offer.extendedDetails = item;
    this.data.offer.extendedDetails.imageUrl = item.iconUrl;

    this.logService.writeLog(`User swapped campaign ${this.data.offer.title} for: ${JSON.stringify(item)}`, LogType.TRACE, LogSource.SWAP_OFFER);

    const swapRewardOpt = JSON.parse(item.isSwapReward)
    this.swapRewardOptions = {
      webhook: swapRewardOpt.webhook,
      printTemplate: swapRewardOpt.printTemplate
    }
    this.isSwapMode = false;

    const { isSplittable, isPrintable } = this.data.offer.extendedDetails;
    if (isSplittable && isPrintable) {
      this.isBreakValueMode = true;
      this.showBreakOptions();
    }
  }

  private getSourceBucket() {
    let sourceBucket = '';
    let { webhook } = this.swapRewardOptions;

    if (webhook === 'PointsUCv1' || !webhook) {
      sourceBucket = this.profileService.playerBalances.a_Points;
    } else if (webhook === 'FreePlayUCv2') {
      sourceBucket = this.profileService.playerBalances.a_FreePlay;
    } else if (webhook === 'TierCredits'){
      sourceBucket = this.profileService.playerBalances.a_Tier;
    } else if (webhook === 'Comps') {
      sourceBucket = this.profileService.playerBalances.a_Comps;
    }

    return sourceBucket;
  }

  public async redeemReward(isRedemptionWithPrinting?: boolean) {
    if (!isRedemptionWithPrinting) {
      this.isLoading = true;
    }
    this.printService.setIsPrinting(true, "CMS Redeem");
    let offerToTransfer = cloneDeep(this.data.offer);
    delete offerToTransfer.extendedDetails;

    try {
      const { errors, status, offerId, amount } = (await lastValueFrom(
        this.offerService.redeemCmsOffer(offerToTransfer)
      )) as any;

      if (!errors?.length && (status === 'Activated' || status === 'Redeemed')) {
        this.data.offer.couponStatus = status;
        this.logService.writeLog(`
            Coupon ${this.data.offer.extendedDetails.title} was sucessfully redeemed.
          `, LogType.TRACE, LogSource.REDEEM
        );

        this.redeemResponse.offerId = offerId;
        this.redeemResponse.amount = amount;

        // deposit cost to user balance
        if (this.data.offer.isSwapped) {
          (async () => {
            const adjRes = await this.profileService.doBalanceAdjustment(
              this.profileService.playerProfile.playerId,
              this.couponCost,
              null,
              'C',
              this.getSourceBucket(),
              this.data.offer.extendedDetails.title
            );

            if (adjRes.error || adjRes.status) {
              this.logService.writeLog(`Balance adjusted fail on post-swap redeem`, LogType.ERROR, LogSource.REDEEM);
              this.snackBar.openFromComponent(SnackbarComponent, {
                panelClass: 'error',
                duration: 3000,
                horizontalPosition: 'end',
                data: {
                  type: 'error',
                  message: 'We could not complete the transaction.',
                },
              });
            } else {
              this.logService.writeLog(`Balance adjusted successfully on post-swap redeem`, LogType.TRACE, LogSource.REDEEM);
            }
          })();
        }

        (async () => {
          await this.profileService.getPlayerBalance(this.profileService.playerProfile.playerId);

          this.snackBar.open(`${this.offerService.successRedeemMessage}`, '', {
            panelClass: 'success',
            duration: 5000,
            horizontalPosition: 'end',
          });

          this.isLoading = false;
          this.closeModal(isRedemptionWithPrinting ? false : true);
        })();
        
      } else {
        this.handleRedeemError(errors[0]?.message);
      }

      if (!isRedemptionWithPrinting) {
        this.isLoading = false;
      }
    } catch (error) {
      this.handleRedeemError(error);
      if (!isRedemptionWithPrinting) {
        this.isLoading = false;
      }
    }

    return this.redeemResponse;
  }

  private handleRedeemError(error: any) {
    this.printService.setIsPrinting(false, "handleRedeemError");
    this.snackBar.openFromComponent(SnackbarComponent, {
      panelClass: 'error',
      duration: 5000,
      horizontalPosition: 'end',
      data: {
        type: 'error',
        message: this.offerService.failRedeemMessage,
      },
    });
    this.logService.writeLog(JSON.stringify(error), LogType.ERROR, LogSource.REDEEM);
  }
}